import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Card from 'components/card';
import { MdClose, MdOpenInNew, MdDelete, MdAdd, MdDownload } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { getPurchase, markReceived, updatePurchase, deletePurchase } from 'interfaces/purchases';
import { getSuppliers, getSupplier, getSupplierProducts } from 'interfaces/supplier';
import { toast } from 'react-toastify';
import { updateProduct, updateProductPrice, getProductsByIds } from 'interfaces/products';
import SaveStatus from "components/actions/SaveStatus";
import { useDisclosure } from '@chakra-ui/hooks';
import DeletePurchase from './DeletePurchase';

interface UpdatePurchaseModalProps {
  id: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const UpdatePurchaseModal: React.FC<UpdatePurchaseModalProps> = ({ id, isOpen, onClose, onUpdated }) => {
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [po, setPo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [receptionDate, setReceptionDate] = useState('');
  const [tempReception, setTempReception] = useState('');
  const [savingReception, setSavingReception] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markingReceived, setMarkingReceived] = useState(false);

  const [lotNumbers, setLotNumbers] = useState<{[key:number]: string}>({});
  const [areas, setAreas] = useState<{[key:number]: string}>({});
  const [dlcs, setDlcs] = useState<{[key:number]: string}>({});
  const [quantities, setQuantities] = useState<{[key:number]: number}>({});
  const [prices, setPrices] = useState<{[key:number]: number}>({});
  const [vats, setVats] = useState<{[key:number]: number}>({});
  const [receptionTemps, setReceptionTemps] = useState<{[key:number]: string}>({});
  const [deliveryFees, setDeliveryFees] = useState<number>(0);
  const [distributeFeesToProducts, setDistributeFeesToProducts] = useState<boolean>(false);
  const [feesDistributed, setFeesDistributed] = useState<boolean>(false);

  const [blNumber, setBlNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [priceUpdated, setPriceUpdated] = useState<{ [key:number]: boolean }>({});

  // Add/remove items
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newProductId, setNewProductId] = useState('');
  const [newProductQty, setNewProductQty] = useState(1);
  const [newProductPrice, setNewProductPrice] = useState(0);

  const autoSaveTimer = useRef<number | null>(null);
  const ignoreAutoSaveRef = useRef(false); // when true auto-save won't run

  const hasQtyPriceChanged = useMemo(() => {
    const items = po?.items || [];
    return items.some((it: any, i: number) => {
      const baseQ = Number(it.quantity ?? it.qty ?? 0);
      const baseP = Number(it.price ?? it.unitPrice ?? 0);
      const baseV = Number(it.vat ?? it.vatPurchase ?? 0);
      const curQ = Number(quantities[i] ?? baseQ);
      const curP = Number(prices[i] ?? baseP);
      const curV = Number(vats[i] ?? baseV);
      const norm = (n: number) => (Number.isFinite(n) ? n : 0);
      return norm(curQ) !== norm(baseQ) || norm(curP) !== norm(baseP) || norm(curV) !== norm(baseV);
    });
  }, [po?.items, quantities, prices, vats]);

  const hasDeliveryFeesChanged = useMemo(() => {
    const baseFees = Number(po?.deliveryFees ?? 0);
    const currentFees = Number(deliveryFees ?? 0);
    return baseFees !== currentFees;
  }, [po?.deliveryFees, deliveryFees]);

  const hasMetaChanged = useMemo(() => {
    return blNumber !== (po?.blNumber || '') || invoiceNumber !== (po?.invoiceNumber || '');
  }, [po?.blNumber, po?.invoiceNumber, blNumber, invoiceNumber]);

  const toYMD = (v: any) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    try {
      return new Date(v).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  const formatMoney = (v: any) =>
    Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

  const getSupplierName = (s: any) => {
    if (!s) return '—';
    if (typeof s === 'string') return s;
    return s.name || s.displayName || s.company || '—';
  };

  const getVatRate = (it: any, index?: number) => {
    if (index !== undefined && vats[index] !== undefined) return vats[index];
    const raw = it?.vat ?? it?.vatPurchase ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const getProdId = (it: any) => {
    const id = it?.productId?._id || it?.productId;
    // Ensure we return a string ID, not an object
    return typeof id === 'string' ? id : (id?._id || id?.toString() || null);
  };

  // Returns true when the product linked to this line has noDlc:true
  const isItemNoDlc = (item: any): boolean => {
    if (item?.productId?.noDlc === true) return true;
    const pid = getProdId(item);
    if (pid) {
      const prod = allProducts.find((p: any) => (p._id || p.id) === pid);
      if (prod?.noDlc === true) return true;
    }
    return false;
  };

  const load = useCallback(async () => {
    if (!id || !isOpen) return;
    setLoading(true);
    try {
      const supRes: any = await getSuppliers();
      setSuppliers(supRes?.docs || supRes?.data || supRes || []);
      const res: any = await getPurchase(id);
      if (!res) {
        toast.error('Bon introuvable');
        setLoading(false);
        return;
      }
      setPo(res);

      const rec = res.receptionDate;
      const today = new Date().toISOString().split('T')[0];
      setReceptionDate(rec ? new Date(rec).toISOString().split('T')[0] : today);

      setTempReception(res.tempReception ?? '');

      // initialize per-line state safely
      const initialLotNumbers: {[key:number]: string} = {};
      const initialDlcs: {[key:number]: string} = {};
      const initialAreas: {[key:number]: string} = {};
      const qts: {[key:number]: number} = {};
      const prs: {[key:number]: number} = {};
      const initialReceptionTemps: {[key:number]: string} = {};
      const vts: {[key:number]: number} = {};

      (res.items || []).forEach((item: any, index: number) => {
        initialLotNumbers[index] = item.lotNumber || '';
        initialDlcs[index] = item.dlc ? toYMD(item.dlc) : '';
        initialAreas[index] = item.area || 'zone-a';
        qts[index] = Number(item.quantity ?? item.qty ?? 0) || 0;
        prs[index] = Number(item.price ?? item.unitPrice ?? 0) || 0;
        vts[index] = Number(item.vat ?? item.vatPurchase ?? 0);
        initialReceptionTemps[index] = item.receptionTemp !== undefined ? String(item.receptionTemp) : '';
      });

      setLotNumbers(initialLotNumbers);
      setDlcs(initialDlcs);
      setAreas(initialAreas);
      setQuantities(qts);
      setPrices(prs);
      setVats(vts);
      setReceptionTemps(initialReceptionTemps);
      setDeliveryFees(Number(res.deliveryFees ?? 0));
      setFeesDistributed(res.feesDistributed ?? false);
      setBlNumber(res.blNumber || '');
      setInvoiceNumber(res.invoiceNumber || '');

      // when purchase is already received, ensure autosave disabled
      if (res.status === 'received') ignoreAutoSaveRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [id, isOpen]);

  useEffect(() => { load(); }, [load]);

  // Load supplier products for the add-item form
  useEffect(() => {
    if (!isOpen || !po) return;
    (async () => {
      const supplierId =
        typeof po.supplier === 'object'
          ? (po.supplier?._id || po.supplier?.id)
          : (po.supplier || po.supplierId || po.supplier_id);
      if (!supplierId) return;

      const supplier = await getSupplier(supplierId);
      let productIds: string[] =
        Array.isArray(supplier?.products)
          ? supplier.products
          : Array.isArray(supplier?.productIds)
          ? supplier.productIds
          : [];

      if (productIds.length === 0) {
        const legacy = await getSupplierProducts(supplierId);
        if (Array.isArray(legacy) && legacy.length > 0) {
          if (legacy[0] && typeof legacy[0] === 'object' && legacy[0].name) {
            const sorted = [...legacy].sort((a: any, b: any) =>
              (a?.name || '').localeCompare(b?.name || '')
            );
            setAllProducts(sorted);
            return;
          }
          if (legacy.every((v: any) => typeof v === 'string')) {
            productIds = legacy;
          }
        }
      }

      if (productIds.length === 0) { setAllProducts([]); return; }

      const products = await getProductsByIds(productIds);
      products.sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || ''));
      setAllProducts(products);
    })();
  }, [isOpen, po]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const supplierName =
    getSupplierName(
      po?.supplier ||
        suppliers.find((s: any) => (s._id) === (po?.supplierId || po?.supplier_id))
    );

  const deliveryPlanned =
    po?.deliveryPurchase ||
    po?.eta ||
    po?.deliveryDate ||
    null;

  // build items payload used by manual saves, mark received etc.
  const buildItemsPayload = () =>
    (po.items || []).map((item: any, index: number) => {
      const q = Number(quantities[index] ?? item.quantity);
      const newPrice = Number(prices[index] ?? item.price);
      const temp = receptionTemps[index];
      return {
        productId: getProdId(item),
        quantity: q,
        price: newPrice,
        vat: vats[index] ?? item.vat ?? item.vatPurchase ?? 0,
        lotNumber: lotNumbers[index] || item.lotNumber || '',
        area: areas[index] || item.area || 'zone-a',
        dlc: isItemNoDlc(item) ? '' : (dlcs[index] ? new Date(dlcs[index]).toISOString() : (item.dlc ? new Date(item.dlc).toISOString() : '')),
        receptionTemp: temp !== '' ? Number(temp) : undefined,
      };
    });

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Function to distribute delivery fees across products
  const handleDistributeFeesToProducts = () => {
    if (!po?.items || deliveryFees <= 0) return;

    // Calculate the total HT amount of the order (excluding fees, only for lines with qty > 0)
    const totalHT = (po.items || []).reduce((sum: number, it: any, i: number) => {
      const qty = Number(quantities[i] ?? it.quantity ?? 0);
      const price = Number(prices[i] ?? it.price ?? 0);
      if (qty <= 0) return sum;
      return sum + (qty * price);
    }, 0);

    if (totalHT === 0) return;

    const newPrices: {[key:number]: number} = {};
    (po.items || []).forEach((it: any, i: number) => {
      const currentPrice = Number(prices[i] ?? it.price ?? 0);
      const qty = Number(quantities[i] ?? it.quantity ?? 0);
      
      if (qty <= 0) return;
      
      const lineTotal = qty * currentPrice;
      
      const linePercentage = lineTotal / totalHT;
      
      const lineFeesToAdd = deliveryFees * linePercentage;
      
      newPrices[i] = round2(currentPrice + (lineFeesToAdd / qty));
    });

    setPrices(newPrices);
    setDistributeFeesToProducts(false);
    setFeesDistributed(true);
    toast.success('Frais de livraison répartis sur les produits');
  };

  const { totalArticlesHT, totalTVA, totalHT, totalTTC } = useMemo(() => {
    const items = po?.items || [];
    let ht = 0;
    let tva = 0;
    items.forEach((it: any, i: number) => {
      const q = Number(quantities[i] ?? it.quantity);
      const pu = Number(prices[i] ?? it.price);
      const lineHT = (isNaN(q) ? 0 : q) * (isNaN(pu) ? 0 : pu);
      const vatRate = Number(vats[i] ?? getVatRate(it)) / 100;
      const lineTVA = lineHT * (vatRate > 0 ? vatRate : 0);
      ht += lineHT;
      tva += lineTVA;
    });
    const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
    const totalHT = round2(ht + (Number.isFinite(fees) ? fees : 0));
    const totalTTC = round2(totalHT + tva);
    return {
      totalArticlesHT: round2(ht),
      totalTVA: round2(tva),
      totalHT,
      totalTTC,
    };
  }, [po?.items, quantities, prices, vats, deliveryFees, feesDistributed]);

  const getMagicToken = (p: any) => {
       if (!p) return null;
       if (p.magicLinkUuid) return p.magicLinkUuid;
       const url = p.magicLink || '';
       if (!url) return null;
       const parts = String(url).split('/');
       return parts[parts.length - 1] || null;
     };
     const magicToken = useMemo(() => getMagicToken(po), [po]);
     const handleOpenMagicLink = () => {
       if (!magicToken) {
         toast.error('Lien fournisseur indisponible');
         return;
       }
       window.open(`/view-purchase/${magicToken}`, '_blank');
     };

  // Sync product prices (manual - kept)
const syncProductsPrices = async () => {
    const items = po?.items || [];

    const updates = items.map(async (item: any, index: number) => {
      const productId = getProdId(item);
      const newPrice = Number(prices[index] ?? item.price);
      const oldPrice = Number(item.price);

      // Validate product ID
      if (!productId || typeof productId !== 'string' || productId.trim() === '') {
        console.warn(`⚠️ ID produit invalide pour l'index ${index}:`, productId);
        return null;
      }
      
      if (!isFinite(newPrice) || newPrice <= 0) return null;
      if (oldPrice === newPrice) return null;

      try {
        await updateProductPrice(productId, newPrice);
        return productId;
      } catch (err) {
        console.warn(`⚠️ Impossible de mettre à jour le produit ${productId}`, err);
        return null;
      }
    });

    await Promise.allSettled(updates);
  };

  // --- AUTO-SAVE ---
  useEffect(() => {
    if (!po || !isOpen) return;
    if (!hasQtyPriceChanged && !hasDeliveryFeesChanged && !hasMetaChanged) return;

    const timer = setTimeout(async () => {
      try {
        setAutoSaving(true);
        setAutoSaveError(null);

        // Build payload items by only modifying what needs to be changed
        const itemsPatch = (po.items || []).map((item: any, index: number) => {
          const quantity = Number(quantities[index] ?? item.quantity ?? 0);
          const price = Number(prices[index] ?? item.price ?? 0);
          const vat = vats[index] ?? item.vat ?? item.vatPurchase ?? 0;
          const temp = receptionTemps[index];
          return {
            ...item,
            quantity,
            price,
            vat,
            lotNumber: lotNumbers[index] ?? item.lotNumber ?? '',
            area: areas[index] ?? item.area ?? 'zone-a',
            dlc: isItemNoDlc(item) ? '' : (dlcs[index] ? new Date(dlcs[index]).toISOString() : item.dlc ?? ''),
            receptionTemp: temp !== '' ? Number(temp) : undefined,
          };
        });


        // Calculate totalHT and totalPrice (TTC)
        const itemsHT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price)), 0);
        const totalVAT = itemsPatch.reduce((sum: any, it: any) => sum + ((Number(it.quantity) * Number(it.price)) * (Number(it.vat ?? 0) / 100)), 0);
        // Add delivery fees only if not distributed (otherwise already included in unit prices)
        const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
        const totalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
        const totalPrice = round2(totalHT + totalVAT);

        const payload: any = {
          _id: po._id,
          items: itemsPatch,
          deliveryFees: fees,
          feesDistributed: feesDistributed,
          totalHT: round2(totalHT),
          totalPrice: round2(totalPrice),
          blNumber,
          invoiceNumber,
        };

        const updated = await updatePurchase(payload);
        if (!updated || updated.error) throw new Error('update failed');

        setLastSavedAt(Date.now());
        onUpdated?.();
      } catch (e) {
        setAutoSaveError('Erreur de sauvegarde');
      } finally {
        setAutoSaving(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [hasQtyPriceChanged, hasDeliveryFeesChanged, hasMetaChanged, po?._id, isOpen, quantities, prices, vats, lotNumbers, areas, dlcs, receptionTemps, deliveryFees, feesDistributed, blNumber, invoiceNumber, onUpdated]);


  useEffect(() => {
    if (!po || !prices) return;

    Object.keys(prices).forEach((key) => {
      const i = Number(key);
      const newPrice = Number(prices[i]);
      const oldPrice = Number(po.items[i]?.price ?? 0);

      if (newPrice !== oldPrice && !isNaN(newPrice)) {
        // AutoSave Product (new purchase price)
        (async () => {
          try {
            const productId = getProdId(po.items[i]);
            
            // Validate product ID
            if (!productId || typeof productId !== 'string' || productId.trim() === '') {
              console.warn(`⚠️ ID produit invalide pour l'auto-save à l'index ${i}:`, productId);
              return;
            }
            
            await updateProductPrice(productId, newPrice);

            setPriceUpdated(prev => ({ ...prev, [i]: true }));
            setTimeout(() => {
              setPriceUpdated(prev => ({ ...prev, [i]: false }));
            }, 1500);
          } catch (err) {
            console.error("Erreur AutoSave prix produit", err);
          }
        })();
      }
    });
  }, [prices]);

  // --- Save full reception (manual) ---
  const handleSaveReception = async () => {
    if (!po) return;
    // require date
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de sauvegarder.');
      return;
    }
    setSavingReception(true);
    ignoreAutoSaveRef.current = true;
    try {
      const payload: any = {
        _id: po._id,
        receptionDate: new Date(receptionDate).toISOString(),
        tempReception: tempReception !== '' ? Number(tempReception) : undefined,
        items: buildItemsPayload(),
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed: feesDistributed,
        totalHT: round2(totalHT),
        totalPrice: round2(totalTTC),
        blNumber,
        invoiceNumber,
      };
      const updated = await updatePurchase(payload);
      if (!updated || updated.error) {
        toast.error('Erreur mise à jour');
        return;
      }
      await syncProductsPrices();
      toast.success('Réception enregistrée');
      onUpdated();
      load();
    } finally {
      setSavingReception(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };

  const handleMarkReceived = async () => {
    if (!po) return;
    if (po.status === 'received') return;
    if (!receptionDate) {
      toast.error('Veuillez renseigner une date de réception avant de marquer reçu.');
      return;
    }
  
    setMarkingReceived(true);
    ignoreAutoSaveRef.current = true;
    if (autoSaveTimer.current) {
      window.clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
  
    try {
      const id = po._id;
  
      // Prepare complete items payload with all details for accurate reception save
      const itemsPatch = buildItemsPayload();

      // Calculare total
      const itemsHT = itemsPatch.reduce((sum: any, it: any) => sum + Number(it.quantity) * Number(it.price), 0);
      const itemsVAT = itemsPatch.reduce((sum: any, it: any) => sum + (Number(it.quantity) * Number(it.price) * (Number(po.items?.[0]?.vat ?? 0) / 100)), 0);
      
      // Add delivery fees only if not distributed
      const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
      const totalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
      const totalPrice = round2(totalHT + itemsVAT);

      //  Save full reception BEFORE markReceived
      const upd = await updatePurchase({
        _id: id,
        id,
        receptionDate: new Date(receptionDate).toISOString(),
        items: itemsPatch,
        deliveryFees: Number(deliveryFees ?? 0),
        feesDistributed: feesDistributed,
        totalHT: round2(totalHT),
        totalPrice: round2(totalPrice),
        blNumber,
        invoiceNumber,
      });
      if (!upd || upd.error) {
        toast.error('Erreur lors de la sauvegarde des données de réception');
        return;
      }
  
      // Add reception details and mark as received
      const res = await markReceived(id);
      if (!res || res.error) {
        toast.error("Erreur lors du passage au statut 'reçu'");
        return;
      }
  
      await syncProductsPrices();
      toast.success('Marqué reçu');
      onUpdated();
      load();
    } finally {
      setMarkingReceived(false);
      ignoreAutoSaveRef.current = false;
      onClose();
    }
  };
  

  // // change only status to 'received' + reception date
  // const handleMarkReceived = async () => {
  //   if (!po) return;
  //   if (po.status === 'received') return;
  //   if (!receptionDate) {
  //     toast.error('Veuillez renseigner une date de réception avant de marquer reçu.');
  //     return;
  //   }

  //   setMarkingReceived(true);
  //   ignoreAutoSaveRef.current = true; // stop autosave while we operate
  //   // clear pending timer
  //   if (autoSaveTimer.current) {
  //     window.clearTimeout(autoSaveTimer.current);
  //     autoSaveTimer.current = null;
  //   }

  //   try {
  //       const id = po._id;
  //       // 1) Màj date de réception
  //       const upd = await updatePurchase({
  //         _id: id,
  //         id,
  //         receptionDate: new Date(receptionDate).toISOString(),
  //       });
  //       if (!upd || upd.error) {
  //         toast.error('Erreur lors de la mise à jour de la date de réception');
  //         return;
  //       }

  //       const res = await markReceived(id);
  //       if (!res || res.error) {
  //         toast.error("Erreur lors du passage au statut 'reçu'");
  //         return;
  //       }

  //       await syncProductsPrices();
  //       toast.success('Marqué reçu');
  //       onUpdated();
  //       load();
  //     } finally {
  //       setMarkingReceived(false);
  //       ignoreAutoSaveRef.current = false;
  //       onClose();
  //     }
  //   };

  const handleMarkPaid = async () => {
    if (!po || po.pay) return;
    setMarkingPaid(true);
    try {
      const updated = await updatePurchase({
        _id: po._id,
        pay: true,
      });
      if (updated && !updated.error) {
        toast.success('Marqué payé');
        onUpdated();
        load();
      } else toast.error('Erreur paiement');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDeletePurchase = async () => {
    if (!po) return;
    try {
      const res = await deletePurchase(po._id);
      if (res && !res.error) {
        toast.success('Commande d\'achat supprimée avec succès');
        onDeleteClose();
        onClose();
        // Trigger parent refresh
        onUpdated();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // --- Persist items immediately (after structural add/remove) ---
  const persistItemsImmediately = async (
    newItems: any[],
    nQ: {[key:number]: number},
    nP: {[key:number]: number},
    nL: {[key:number]: string},
    nA: {[key:number]: string},
    nD: {[key:number]: string},
    nT: {[key:number]: string}
  ) => {
    try {
      setAutoSaving(true);
      setAutoSaveError(null);
      const itemsPatch = newItems.map((item: any, index: number) => {
        const quantity = Number(nQ[index] ?? item.quantity ?? 0);
        const price = Number(nP[index] ?? item.price ?? 0);
        const temp = nT[index];
        return {
          ...item,
          productId: getProdId(item),
          quantity,
          price,
          lotNumber: nL[index] ?? item.lotNumber ?? '',
          area: nA[index] ?? item.area ?? 'zone-a',
          dlc: isItemNoDlc(item) ? '' : (nD[index] ? new Date(nD[index]).toISOString() : (item.dlc ? new Date(item.dlc).toISOString() : '')),
          receptionTemp: temp !== '' ? Number(temp) : undefined,
        };
      });
      const itemsHT = itemsPatch.reduce((sum: number, it: any) => sum + (Number(it.quantity) * Number(it.price)), 0);
      const totalVAT = itemsPatch.reduce((sum: number, it: any) => sum + ((Number(it.quantity) * Number(it.price)) * (Number(it.vat ?? it.vatPurchase ?? 0) / 100)), 0);
      const fees = feesDistributed ? 0 : Number(deliveryFees ?? 0);
      const calcTotalHT = round2(itemsHT + (Number.isFinite(fees) ? fees : 0));
      const calcTotalPrice = round2(calcTotalHT + totalVAT);
      const updated = await updatePurchase({
        _id: po._id,
        items: itemsPatch,
        deliveryFees: fees,
        feesDistributed,
        totalHT: calcTotalHT,
        totalPrice: calcTotalPrice,
      });
      if (!updated || updated.error) throw new Error('update failed');
      setLastSavedAt(Date.now());
      onUpdated?.();
    } catch (e) {
      setAutoSaveError('Erreur de sauvegarde');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setAutoSaving(false);
    }
  };

  // --- Remove an item ---
  const removeItem = async (indexToRemove: number) => {
    const newItems = (po.items || []).filter((_: any, i: number) => i !== indexToRemove);
    const rebuildMap = <T,>(map: {[key:number]: T}): {[key:number]: T} => {
      const result: {[key:number]: T} = {};
      Object.keys(map).forEach((k) => {
        const i = Number(k);
        if (i < indexToRemove) result[i] = map[i];
        else if (i > indexToRemove) result[i - 1] = map[i];
      });
      return result;
    };
    const nQ = rebuildMap(quantities);
    const nP = rebuildMap(prices);
    const nV = rebuildMap(vats);
    const nL = rebuildMap(lotNumbers);
    const nD = rebuildMap(dlcs);
    const nA = rebuildMap(areas);
    const nT = rebuildMap(receptionTemps);
    setPo((prev: any) => ({ ...prev, items: newItems }));
    setQuantities(nQ);
    setPrices(nP);
    setVats(nV);
    setLotNumbers(nL);
    setDlcs(nD);
    setAreas(nA);
    setReceptionTemps(nT);
    await persistItemsImmediately(newItems, nQ, nP, nL, nA, nD, nT);
  };

  // --- Add a new item ---
  const handleAddItem = async () => {
    if (!newProductId) { toast.error('Sélectionnez un produit'); return; }
    const prod = allProducts.find((p: any) => (p._id || p.id) === newProductId);
    if (!prod) return;
    const newIndex = (po.items || []).length;
    const newItem = {
      productId: prod,
      quantity: newProductQty,
      price: newProductPrice,
      lotNumber: '',
      area: 'zone-a',
      dlc: '',
      vat: prod.vatPurchase ?? prod.vat ?? 0,
    };
    const newItems = [...(po.items || []), newItem];
    const nQ = { ...quantities, [newIndex]: newProductQty };
    const nP = { ...prices, [newIndex]: newProductPrice };
    const nV = { ...vats, [newIndex]: Number(prod.vatPurchase ?? prod.vat ?? 0) };
    const nL = { ...lotNumbers, [newIndex]: '' };
    const nD = { ...dlcs, [newIndex]: '' };
    const nA = { ...areas, [newIndex]: 'zone-a' };
    const nT = { ...receptionTemps, [newIndex]: '' };
    setPo((prev: any) => ({ ...prev, items: newItems }));
    setQuantities(nQ);
    setPrices(nP);
    setVats(nV);
    setLotNumbers(nL);
    setDlcs(nD);
    setAreas(nA);
    setReceptionTemps(nT);
    setNewProductId('');
    setNewProductQty(1);
    setNewProductPrice(0);
    setShowAddRow(false);
    await persistItemsImmediately(newItems, nQ, nP, nL, nA, nD, nT);
    toast.success('Article ajouté');
  };

  // --- Export XLSX ---
  const handleExportXlsx = () => {
    if (!po) return;
    const rows = (po.items || []).map((it: any, i: number) => ({
      'Numéro de commande': po.purchaseId || '',
      'Référence client': '',
      'Type de process': '',
      'Fournisseur': supplierName,
      'Date de réception prévue': deliveryPlanned
        ? new Date(deliveryPlanned).toLocaleDateString('fr-FR')
        : '',
      'Nbr de palettes attendu': '',
      'Nbr de colis attendu': '',
      'SKU produit': it?.productId?.ref || '',
      'Quantité produit': Number(quantities[i] ?? it.quantity ?? 0),
      'Poids total': '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commande');
    XLSX.writeFile(wb, `BC_${po.purchaseId || po._id}.xlsx`);
  };

  // Permissions: owner (no ownerId in localStorage) OR 'all' OR 'purchases:override'
  const _storedPermissions = JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
  const _isOwner = !localStorage.getItem('ownerId');
  const canOverride = _isOwner || _storedPermissions.includes('all') || _storedPermissions.includes('purchases:override');

  // disable editing when already received, unless user can override
  const isLocked = po?.status === 'received' && !canOverride;
  const canDelete = po?.status === 'pending' || (canOverride && po?.status === 'received');

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <Card extra="p-6 border border-gray-200 rounded-2xl shadow-lg bg-white dark:bg-navy-800">
            {loading && (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin h-6 w-6 border-b-2 border-brand-500 rounded-full mr-2" />
                <p className="text-sm text-gray-500">Chargement…</p>
              </div>
            )}

            {!loading && po && (
            <>
              {/* Header */}
              <div className="flex justify-between items-start mb-6 border-b border-gray-200 pb-4">
                <div>
                  <h2 className="text-xl font-semibold text-navy-700">
                    Bon de commande <span className="text-[#0260CB]">{po.purchaseId}</span>
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Fournisseur : <span className="font-medium text-gray-900">{supplierName}</span>
                  </p>
                  {po.createdBy && (
                    <p className="text-xs text-gray-400 mt-1">
                      Créé par : {typeof po.createdBy === 'string' ? po.createdBy : po.createdBy?.fullName || po.createdBy?.email || '—'}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Statut : {po.status || '—'}
                    </span>
                    {po.pay ? (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-200 text-emerald-900">
                       Payé
                     </span>
                   ) : (
                     <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                       En attente de paiement
                     </span>
                   )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <SaveStatus
                    status={
                      po.status === 'received'
                        ? 'saved' // lock visual state when received
                        : autoSaving
                          ? 'saving'
                          : autoSaveError
                            ? 'error'
                            : lastSavedAt
                              ? 'saved'
                              : 'idle'
                    }
                  />
                  <button
                    onClick={handleExportXlsx}
                    title="Exporter en XLSX"
                    className="px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                  >
                    <MdDownload className="w-4 h-4" />
                    Export XLSX
                  </button>
                  <button
                   onClick={handleOpenMagicLink}
                   disabled={!magicToken}
                   title="Voir la page fournisseur"
                   className="px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                 >
                   <MdOpenInNew className="w-4 h-4" />
                   Lien fournisseur
                 </button>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Infos */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Date commande</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {po.purchaseDate ? new Date(po.purchaseDate).toLocaleDateString('fr-FR') : po.createdAt ? new Date(po.createdAt).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Livraison prévue</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {deliveryPlanned ? new Date(deliveryPlanned).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Réception (date) *</label>
                  <input
                    type="date"
                    value={receptionDate}
                    onChange={(e) => setReceptionDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Température réception (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={tempReception}
                    onChange={(e) => setTempReception(e.target.value)}
                    placeholder="Ex: 3.2"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                    disabled={isLocked}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">N° BL (Bon de livraison)</label>
                  <input
                    type="text"
                    value={blNumber}
                    onChange={(e) => setBlNumber(e.target.value)}
                    placeholder="Ex: BL-2026-001"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">N° Facture fournisseur</label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Ex: FAC-2026-001"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-brand-500 outline-none"
                  />
                </div>
              </div>

              {/* Articles */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-navy-700 mb-3">Articles</h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full text-xs text-gray-700">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left py-3 px-4">Produit</th>
                        <th className="text-right py-3 px-4">Quantité</th>
                        <th className="text-right py-3 px-4">Prix unitaire</th>
                        <th className="text-right py-3 px-4">TVA (%)</th>
                        <th className="text-right py-3 px-4">Sous-total</th>
                        <th className="text-left py-3 px-4">Numéro de lot</th>
                        <th className="text-left py-3 px-4">DLC</th>
                        <th className="text-left py-3 px-4">Zone</th>
                        <th className="text-left py-3 px-4">Température (°C)</th>
                        {!isLocked && <th className="py-3 px-4"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(po.items || []).map((it: any, i: number) => {
                        const q = Number(quantities[i] ?? it.quantity);
                        const pu = Number(prices[i] ?? it.price);
                        return (
                          <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-800">
                              {it?.productId?.name} ({it?.productId?.ref})
                            </td>

                            <td className="py-3 px-4 text-right">
                              <input
                                type="number"
                                min={0}
                                value={isNaN(q) ? 0 : q}
                                onChange={(e) => setQuantities(prev => ({ ...prev, [i]: Number(e.target.value) }))}
                                className="w-24 text-right rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>

                            <td className="py-3 px-4 text-right align-top">
                              <div className="flex flex-col items-end">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.1"
                                  value={isNaN(pu) ? 0 : pu}
                                  onChange={(e) =>
                                    setPrices((prev) => ({ ...prev, [i]: Number(e.target.value) }))
                                  }
                                  className="w-28 text-right rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                  disabled={isLocked}
                                />
                                {priceUpdated[i] && (
                                  <span className="mt-1 text-[11px] text-green-500 bg-emerald-50 rounded px-2 py-0.5">
                                    Prix d'achat produit maj
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="py-3 px-4 text-right">
                              <input
                                type="number"
                                min={0}
                                step="0.5"
                                value={vats[i] ?? getVatRate(it)}
                                onChange={(e) => setVats(prev => ({ ...prev, [i]: Number(e.target.value) }))}
                                className="w-16 text-right rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>


                            <td className="py-3 px-4 text-right">
                              {formatMoney((isNaN(pu) ? 0 : pu) * (isNaN(q) ? 0 : q))}
                            </td>

                            <td className="py-3 px-4">
                              <input
                                type="text"
                                value={lotNumbers[i] || ''}
                                onChange={(e) => setLotNumbers(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder="Numéro de lot"
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>

                            <td className="py-3 px-4">
                              {isItemNoDlc(it) ? (
                                <span className="text-xs text-gray-400 italic">Sans DLC</span>
                              ) : (
                                <input
                                  type="date"
                                  value={dlcs[i] || ''}
                                  onChange={(e) => setDlcs(prev => ({ ...prev, [i]: e.target.value }))}
                                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                  disabled={isLocked}
                                />
                              )}
                            </td>

                            <td className="py-3 px-4">
                              <select
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none bg-white"
                                value={areas[i] || 'zone-a'}
                                onChange={(e) => setAreas(prev => ({ ...prev, [i]: e.target.value }))}
                                disabled={isLocked}
                              >
                                {[
                                  { v: 'zone-a', l: 'Zone A' },
                                  { v: 'zone-b', l: 'Zone B' },
                                  { v: 'zone-c', l: 'Zone C' },
                                ].map((item, index) => (
                                  <option key={index} value={item.v}>{item.l}</option>
                                ))}
                              </select>
                            </td>

                            <td className="py-3 px-4">
                              <input
                                type="number"
                                step="0.1"
                                value={receptionTemps[i] || ''}
                                onChange={(e) => setReceptionTemps(prev => ({ ...prev, [i]: e.target.value }))}
                                placeholder="Ex: 3.2"
                                className="w-20 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
                                disabled={isLocked}
                              />
                            </td>
                            {!isLocked && (
                              <td className="py-3 px-4 text-center">
                                <button
                                  onClick={() => removeItem(i)}
                                  className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                                  title="Supprimer cet article"
                                >
                                  <MdDelete className="w-4 h-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {(!po.items || po.items.length === 0) && (
                        <tr>
                          <td colSpan={isLocked ? 9 : 10} className="py-4 text-center text-gray-400">Aucun article</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!isLocked && (
                  <div className="mt-3">
                    {!showAddRow ? (
                      <button
                        onClick={() => setShowAddRow(true)}
                        className="flex items-center gap-1 text-xs text-[#0260CB] hover:text-[#01499b] px-3 py-1.5 rounded border border-dashed border-[#0260CB] hover:bg-blue-50"
                      >
                        <MdAdd className="w-4 h-4" />
                        Ajouter un article
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-end gap-2 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex-1 min-w-[200px]">
                          <label className="text-xs text-gray-500 block mb-1">Produit</label>
                          <select
                            value={newProductId}
                            onChange={(e) => {
                              setNewProductId(e.target.value);
                              const prod = allProducts.find((p: any) => (p._id || p.id) === e.target.value);
                              if (prod) setNewProductPrice(Number(prod.purchasePrice ?? prod.unitPrice ?? 0));
                            }}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none bg-white"
                          >
                            <option value="">Sélectionner un produit...</option>
                            {allProducts
                              .filter((p: any) => {
                                const pid = p._id || p.id;
                                return !(po.items || []).some((it: any) => getProdId(it) === pid);
                              })
                              .map((p: any) => {
                                const pid = p._id || p.id;
                                const pname = p.name || p.displayName || '(sans nom)';
                                const pref = p.ref ? ` (${p.ref})` : '';
                                return <option key={pid} value={pid}>{pname}{pref}</option>;
                              })}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Quantité</label>
                          <input
                            type="number"
                            min={1}
                            value={newProductQty}
                            onChange={(e) => setNewProductQty(Number(e.target.value))}
                            className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Prix unitaire (€)</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={newProductPrice}
                            onChange={(e) => setNewProductPrice(Number(e.target.value))}
                            className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-brand-500 outline-none"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleAddItem}
                            className="px-3 py-1.5 bg-[#0260CB] text-white rounded-md text-xs hover:bg-[#01499b]"
                          >
                            Ajouter
                          </button>
                          <button
                            onClick={() => { setShowAddRow(false); setNewProductId(''); setNewProductQty(1); setNewProductPrice(0); }}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md text-xs hover:bg-gray-200"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4">
                <div className="text-sm text-navy-700 flex flex-col items-end gap-0.5">
                  <div className="flex gap-4"><span className="text-gray-500">Sous-total articles (HT)</span><span className="font-medium">{formatMoney(totalArticlesHT)}</span></div>
                  <div className="flex gap-4 items-center">
                    <span className="text-gray-500">Frais de livraison</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={isNaN(deliveryFees) ? 0 : deliveryFees}
                      onChange={(e) => setDeliveryFees(Number(e.target.value))}
                      className="w-28 text-right rounded-md border border-gray-300 px-2 py-1 text-xs font-medium focus:border-brand-500 outline-none"
                      disabled={isLocked}
                    />
                  </div>
                  
                  {/* Option fees repartition */}
                  {deliveryFees > 0 && !isLocked && !feesDistributed && (
                    <div className="flex gap-2 items-center mt-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200">
                      <input
                        type="checkbox"
                        id="distributeFeesToProducts"
                        checked={distributeFeesToProducts}
                        onChange={(e) => setDistributeFeesToProducts(e.target.checked)}
                        className="w-4 h-4 text-[#0260CB] border-gray-300 rounded focus:ring-[#0260CB]"
                      />
                      <label htmlFor="distributeFeesToProducts" className="text-xs text-gray-700 cursor-pointer">
                        Répartir les frais sur les produits (proportionnellement aux quantités)
                      </label>
                      {distributeFeesToProducts && (
                        <button
                          onClick={handleDistributeFeesToProducts}
                          className="ml-2 px-3 py-1 bg-[#0260CB] text-white rounded-md text-xs hover:bg-[#01499b]"
                        >
                          Appliquer
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Message information on fees */}
                  {feesDistributed && deliveryFees > 0 && (
                    <div className="flex gap-2 items-start mt-2 px-3 py-2 bg-emerald-50 rounded-md border border-emerald-200">
                      <svg className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs text-emerald-800">
                        Les frais de livraison ({formatMoney(deliveryFees)}) ont été répartis sur les prix unitaires des produits proportionnellement aux quantités commandées.
                      </span>
                    </div>
                  )}

                  <div className="flex gap-4"><span className="text-gray-500">TVA totale</span><span className="font-medium">{formatMoney(totalTVA)}</span></div>
                  <div className="flex gap-4 border-t pt-1 mt-1"><span className="font-semibold">Total TTC</span><span className="font-semibold">{formatMoney(totalTTC)}</span></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-gray-200">
                <div className="flex gap-3">
                  {canDelete && (
                    <button 
                      onClick={onDeleteOpen} 
                      className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-xs flex items-center gap-2"
                    >
                      Supprimer la commande
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  {!po.pay && (
                    <button onClick={handleMarkPaid} disabled={markingPaid} className="px-4 py-2 bg-emerald-500 text-emerald-500 border border-emerald-200 rounded-lg hover:bg-emerald-600 text-xs flex items-center gap-2 disabled:opacity-50">
                      {markingPaid ? 'Marquage…' : 'Marquer payé'}
                    </button>
                  )}

                  <button
                    onClick={po.status === 'pending' ? handleMarkReceived : handleSaveReception}
                    disabled={(po.status === 'pending' ? markingReceived || savingReception : savingReception) || isLocked}
                    className="px-4 py-2 bg-[#0260CB] text-white rounded-lg hover:bg-[#01499b] flex items-center gap-2 text-xs disabled:opacity-50"
                  >
                    {po.status === 'pending' ? (markingReceived ? 'Enregistrement…' : 'Marquer reçu') : (savingReception ? 'Mise à jour…' : 'Mettre à jour réception')}
                  </button>

                  <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs">Fermer</button>
                </div>
              </div>
            </>
            )}
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal - rendered outside to be on top */}
      <DeletePurchase
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        context="de la commande d'achat"
        description={`Êtes-vous sûr de vouloir supprimer définitivement le bon de commande ${po?.purchaseId || ''} ? Cette action est irréversible.`}
        deleteFunction={handleDeletePurchase}
      />
    </>
  );
};

export default UpdatePurchaseModal;
